import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '@ds';
import { cmToFtInString, ftInToCmString, getRoundedInputValue } from '@features/forms/DonorProfileForm/utils';
import { DonorProfileFormValues } from '../types';
import { UnitLabel } from './UnitLabel';

/**
 * Height may be entered in either cm or ft/in (spec: "system converts between the two").
 * `donorHeight` (cm) is the canonical RHF field; ft/in are local display state kept in
 * sync — whichever side the user types into becomes the source for that change.
 */
export const HeightInput = () => {
  const { control, setValue, getValues } = useFormContext<DonorProfileFormValues>();
  const [{ ft, in: inches }, setFtIn] = useState(() => cmToFtInString(getValues('donorHeight') ?? ''));

  return (
    <Controller
      name="donorHeight"
      control={control}
      render={({ field, fieldState: { invalid, error } }) => {
        const handleFtInChange = (nextFt: string, nextIn: string) => {
          const nextInRounded = getRoundedInputValue(nextIn, 2);

          setFtIn({ ft: nextFt, in: nextInRounded });
          setValue('donorHeight', ftInToCmString(nextFt, nextInRounded), {
            shouldValidate: true,
            shouldDirty: true,
          });
        };

        return (
          <div className="flex items-start gap-4">
            <div className="w-[112px]">
              <Input
                type="number"
                aria-label="Donor height in centimeters"
                endContent={<UnitLabel unit="cm" />}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={getRoundedInputValue(field.value, 2)}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  setFtIn(cmToFtInString(e.target.value));
                }}
              />
            </div>

            <span className="text-base-30 pt-2">/</span>

            <div className="flex flex-col">
              <div className="flex items-start gap-2">
                <div className="w-[84px]">
                  <Input
                    type="number"
                    aria-label="Donor height in feet"
                    endContent={<UnitLabel unit="ft" />}
                    isInvalid={invalid}
                    value={ft}
                    onChange={(e) => handleFtInChange(e.target.value, inches)}
                  />
                </div>

                <div className="w-[92px]">
                  <Input
                    type="number"
                    aria-label="Donor height in inches"
                    endContent={<UnitLabel unit="in" />}
                    isInvalid={invalid}
                    value={inches}
                    onChange={(e) => handleFtInChange(ft, e.target.value)}
                  />
                </div>
              </div>

              {invalid && error?.message ? <span className="text-tiny text-danger pt-1">{error.message}</span> : null}
            </div>
          </div>
        );
      }}
    />
  );
};
